import { z } from 'zod';
import { contentFormatCodeSchema } from './content-format.js';
import { generationDtoSchema } from './generation.js';
import { carouselRenderDtoSchema } from './carousel.js';
import { chargedViaSchema } from './credits.js';

export const sessionStatusSchema = z.enum([
  'draft',
  'answering',
  'generating',
  'completed',
  'failed',
]);
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

// Single answer value (trimmed). The questionnaire UI caps its own textarea at 2000;
// the long limit exists for the own-text carousel (Iter 20), where a_1 is a whole post.
export const ANSWER_MAX_CHARS = 10_000;
export const answerValueSchema = z.string().trim().min(1).max(ANSWER_MAX_CHARS);

// Map of answers: keys are a_1..a_N (range bound enforced server-side via idea.questions.length)
// or one of the fixed profile keys (niche, product, target_audience). Profile keys are only
// valid when the idea's template references the corresponding placeholder — that check is server-side.
export const sessionAnswersSchema = z.record(
  z.string().regex(/^(a_\d+|niche|product|target_audience)$/),
  answerValueSchema,
);

// Full session entity (snake_case wire format)
export const sessionDetailSchema = z.object({
  id: z.number().int().positive(),
  user_id: z.number().int().positive(),
  idea_id: z.number().int().positive().nullable(),
  format_id: z.number().int().positive(),
  status: sessionStatusSchema,
  answers: z.record(z.string(), z.string()),
  free_rewrites_used: z.number().int().min(0).max(1),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type SessionDetail = z.infer<typeof sessionDetailSchema>;

// POST /sessions — by idea id (gallery) or by idea code (hidden ideas such as the
// own-text carousel, which the TMA reaches without knowing the row id).
export const sessionsCreateBodySchema = z.union([
  z.object({ idea_id: z.number().int().positive() }),
  z.object({ idea_code: z.string().min(1).max(64) }),
]);
export type SessionsCreateBody = z.infer<typeof sessionsCreateBodySchema>;

export const sessionsCreateResponseSchema = z.object({
  session: sessionDetailSchema,
  idea: z.object({
    id: z.number().int().positive(),
    code: z.string(),
    title: z.string(),
    questions: z.array(z.string()),
  }),
  resumed: z.boolean(),
  requires_niche: z.boolean(),
  requires_product: z.boolean(),
  requires_target_audience: z.boolean(),
  profile: z.object({
    niche: z.string().nullable(),
    product: z.string().nullable(),
    target_audience: z.string().nullable(),
  }),
});
export type SessionsCreateResponse = z.infer<typeof sessionsCreateResponseSchema>;

// Rewrite (Iter 5)
// ----------------------------------------------------------------------------

export const rewriteCostSchema = z.enum(['free', 'paid', 'blocked']);
export type RewriteCost = z.infer<typeof rewriteCostSchema>;

// POST /sessions/:id/rewrite
export const sessionsRewriteBodySchema = z.object({
  feedback: z.string().trim().min(3).max(500),
});
export type SessionsRewriteBody = z.infer<typeof sessionsRewriteBodySchema>;

export const sessionRewriteResponseSchema = z.object({
  generation: generationDtoSchema,
  balance: z.number().int().nonnegative(),
  session_status: z.literal('completed'),
  is_free_rewrite: z.boolean(),
  free_rewrites_used: z.number().int().min(0).max(1),
  render: carouselRenderDtoSchema.nullable(),
  charged_via: chargedViaSchema,
  // Cost of the NEXT rewrite, computed server-side (knows turbo and prices).
  rewrite_cost: rewriteCostSchema,
});
export type SessionRewriteResponse = z.infer<typeof sessionRewriteResponseSchema>;

// GET /sessions/:id
export const sessionGetResponseSchema = z.object({
  session: sessionDetailSchema,
  idea: z.object({
    id: z.number().int().positive(),
    code: z.string(),
    title: z.string(),
    questions: z.array(z.string()),
    is_active: z.boolean(),
  }),
  stale: z.boolean(),
  last_generation: generationDtoSchema.nullable(),
  rewrite_cost: rewriteCostSchema,
  requires_niche: z.boolean(),
  requires_product: z.boolean(),
  requires_target_audience: z.boolean(),
  profile: z.object({
    niche: z.string().nullable(),
    product: z.string().nullable(),
    target_audience: z.string().nullable(),
  }),
  format_code: contentFormatCodeSchema,
  style_code: z.string().nullable(),
  nick: z.string().nullable(),
  render: carouselRenderDtoSchema.nullable(),
});
export type SessionGetResponse = z.infer<typeof sessionGetResponseSchema>;

// PATCH /sessions/:id/answers
export const sessionsAnswersBodySchema = z.object({
  answers: sessionAnswersSchema,
});
export type SessionsAnswersBody = z.infer<typeof sessionsAnswersBodySchema>;

// GET /sessions/drafts
export const sessionDraftItemSchema = z.object({
  id: z.number().int().positive(),
  idea: z.object({
    id: z.number().int().positive(),
    code: z.string(),
    title: z.string(),
    questions_count: z.number().int().nonnegative(),
  }),
  answered_count: z.number().int().nonnegative(),
  updated_at: z.string().datetime(),
});
export type SessionDraftItem = z.infer<typeof sessionDraftItemSchema>;

export const sessionsDraftsResponseSchema = z.object({
  drafts: z.array(sessionDraftItemSchema),
});
export type SessionsDraftsResponse = z.infer<typeof sessionsDraftsResponseSchema>;

// POST /sessions/:id/transcribe
export const transcribeResponseSchema = z.object({
  text: z.string(),
});
export type TranscribeResponse = z.infer<typeof transcribeResponseSchema>;

// History list (Iter 8)
// ----------------------------------------------------------------------------

export const sessionHistoryItemSchema = z.object({
  id: z.number().int().positive(),
  idea_title: z.string(),
  format_code: contentFormatCodeSchema,
  /** First slide of the last render in the session's style; null for Reels. */
  thumbnail_url: z.string().nullable(),
  last_generation: z
    .object({
      content_preview: z.string(),
      created_at: z.string().datetime(),
    })
    .nullable(),
  updated_at: z.string().datetime(),
});
export type SessionHistoryItem = z.infer<typeof sessionHistoryItemSchema>;

export const sessionsHistoryResponseSchema = z.object({
  items: z.array(sessionHistoryItemSchema),
  has_more: z.boolean(),
});
export type SessionsHistoryResponse = z.infer<typeof sessionsHistoryResponseSchema>;
